import * as api from './intrastatsApi';
import { ConfirmationModal, ModalProvider, useModal } from '../generalcomponents/ModalProvider';
import { DetailViewShell, FormRow, sx13 } from '../common/detailView';
import { TextField, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import type { Intrastat } from './intrastatsApi';
import { useDetailView } from '../common/useDetailView';
import { useTranslation } from 'react-i18next';

interface Props {
    intrastatId: number | null;
}

type StringField =
    | 'code'
    | 'internalDescription'
    | 'externalCode1'
    | 'externalDescription1'
    | 'externalCode2'
    | 'externalDescription2'
    | 'externalCode3'
    | 'externalDescription3';

const FIELDS: Array<{ key: StringField; labelKey: string; maxLength: number }> = [
    { key: 'code', labelKey: 'intrastat.code', maxLength: 12 },
    { key: 'internalDescription', labelKey: 'intrastat.internalDescription', maxLength: 100 },
    { key: 'externalCode1', labelKey: 'intrastat.externalCode1', maxLength: 12 },
    { key: 'externalDescription1', labelKey: 'intrastat.externalDescription1', maxLength: 100 },
    { key: 'externalCode2', labelKey: 'intrastat.externalCode2', maxLength: 12 },
    { key: 'externalDescription2', labelKey: 'intrastat.externalDescription2', maxLength: 100 },
    { key: 'externalCode3', labelKey: 'intrastat.externalCode3', maxLength: 12 },
    { key: 'externalDescription3', labelKey: 'intrastat.externalDescription3', maxLength: 100 }
];

export function IntrastatView(props: Props) {
    return (
        <ModalProvider>
            <IntrastatViewInner {...props} />
        </ModalProvider>
    );
}

function IntrastatViewInner({ intrastatId }: Props) {
    const { t } = useTranslation();
    const { openModal } = useModal();
    const detail = useDetailView<Intrastat>();

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            if (intrastatId === null) {
                detail.setItem({ id: 0, code: '' });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getIntrastat(intrastatId);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [intrastatId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const update = (field: StringField, value: string) => {
        detail.update(field, value as Intrastat[StringField]);
    };

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const response = await api.saveIntrastat(item);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = () => {
        if (!item) return;
        openModal(
            <ConfirmationModal
                header={t('common.delete')}
                message={t('intrastat.confirmDelete')}
                onModalResponse={(confirmed) => { if (confirmed) performDelete(); }}
            />
        );
    };

    const performDelete = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            await api.deleteIntrastat(item.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<Intrastat>
            featureKey="devFeature_reactIntrastatView"
            title={`${item?.code ?? ''} ${item?.internalDescription ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(intrastat) => (
                <>
                    {FIELDS.map((f) => {
                        // The legacy view hides the code in display mode for existing
                        // rows unless the row is in edit mode. Keep that behavior for
                        // the code field only.
                        const hideDisplay = f.key === 'code' && detail.isNew;
                        return (
                            <FormRow key={f.key} label={t(f.labelKey)}>
                                {!detail.isEditing && !hideDisplay ? (
                                    <Typography sx={sx13}>{intrastat[f.key]}</Typography>
                                ) : (
                                    <TextField size="small" fullWidth
                                        inputProps={{ maxLength: f.maxLength }}
                                        value={intrastat[f.key] ?? ''}
                                        onChange={(e) => update(f.key, e.target.value)} />
                                )}
                            </FormRow>
                        );
                    })}
                </>
            )}
        </DetailViewShell>
    );
}
